import { useState, useRef } from 'react';

export default function Home() {
  const [activeTab, setActiveTab] = useState('transcription');
  const [transcripts, setTranscripts] = useState([]);
  const [fineTuningAudios, setFineTuningAudios] = useState([]);
  const [progress, setProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const arrayBuffer = await file.arrayBuffer();
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      processAudioBuffer(audioBuffer);
    }
  };

  const processAudioBuffer = async (audioBuffer) => {
    const sampleRate = audioBuffer.sampleRate;
    const chunkSize = 10 * sampleRate;
    const totalSamples = audioBuffer.length;
    let offset = 0;

    setIsProcessing(true);

    while (offset < totalSamples) {
      const chunk = audioBuffer.getChannelData(0).slice(offset, offset + chunkSize);
      offset += chunkSize;
      await sendAudioChunkFromBuffer(chunk, sampleRate);

      const currentProgress = Math.min((offset / totalSamples) * 100, 100);
      setProgress(currentProgress);
    }

    setTimeout(() => setIsProcessing(false), 1000);
  };

  const sendAudioChunkFromBuffer = async (buffer, sampleRate) => {
    const audioBlob = await convertToWav(buffer, sampleRate);
    const formData = new FormData();
    formData.append('audio', audioBlob, 'chunk.wav');

    try {
      const response = await fetch(`${apiBaseUrl}/api/fine_tuning/transcribe?model=openai`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        const openaiTranscript = result.transcript;

        const correctionResult = await correctTranscript(openaiTranscript);
        setTranscripts((prev) => [
          ...prev,
          {
            audioBlob,
            rawTranscript: openaiTranscript,
            correctedTranscript: correctionResult.correctedTranscript,
            complexityTermsScore: correctionResult.complexityTermsScore,
          },
        ]);
      } else {
        console.error('Failed to fetch transcript:', response.statusText);
      }
    } catch (error) {
      console.error('Error sending audio chunk:', error);
    }
  };

  const convertToWav = (float32Array, sampleRate) => {
    return new Promise((resolve) => {
      const wavBuffer = encodeWav(float32Array, sampleRate);
      resolve(new Blob([wavBuffer], { type: 'audio/wav' }));
    });
  };

  const encodeWav = (samples, sampleRate) => {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);

    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + samples.length * 2, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(view, 36, 'data');
    view.setUint32(40, samples.length * 2, true);

    floatTo16BitPCM(view, 44, samples);

    return buffer;
  };

  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  const floatTo16BitPCM = (output, offset, input) => {
    for (let i = 0; i < input.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, input[i]));
      output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }
  };

  const correctTranscript = async (transcript) => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/fine_tuning/correct_transcript`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcript }),
      });

      if (response.ok) {
        const content = await response.text();
        const contentJson = JSON.parse(content);
        return { correctedTranscript: contentJson.correctedTranscript, complexityTermsScore: contentJson.complexityTermsScore };
      } else {
        console.error('Failed to correct transcript:', response.statusText);
        return { correctedTranscript: '', complexityTermsScore: 0 };
      }
    } catch (error) {
      console.error('Error correcting transcript:', error);
      return { correctedTranscript: '', complexityTermsScore: 0 };
    }
  };

  const handleSubmitCorrection = async (audioBlob, transcriptText, buttonRef, textareaRef) => {
    const formData = new FormData();
    formData.append('file', audioBlob, 'chunk.wav');
    formData.append('transcript', transcriptText);
    formData.append('source', 'manual_addition');

    try {
      const response = await fetch(`${apiBaseUrl}/api/fine_tuning/corrections`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        buttonRef.current.style.display = 'none';
        textareaRef.current.disabled = true;
        console.log('Upload successful');
      } else {
        console.error('Upload failed:', response.statusText);
      }
    } catch (error) {
      console.error('Error uploading:', error);
    }
  };

  const fetchFineTuningAudios = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/fine_tuning/corrections/all`);
      if (response.ok) {
        const data = await response.json();
        setFineTuningAudios(data.corrections);
      } else {
        console.error('Failed to fetch fine-tuning audios:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching fine-tuning audios:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      {isProcessing && (
        <div className="fixed top-0 left-0 w-full z-50">
          <div className="bg-blue-200 h-1 w-full relative">
            <div
              className="bg-blue-600 h-1 transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="text-center text-sm bg-blue-50 py-1 text-blue-800 shadow-md">
            Processing Transcript... {Math.floor(progress)}%
          </div>
        </div>
      )}

      <header className="bg-white shadow p-4 flex justify-between items-center">
        <h1 className="text-xl font-bold text-gray-800">Airwave Whisper FineTuning</h1>
        <div className="flex space-x-4">
          <button
            onClick={() => setActiveTab('transcription')}
            className={`px-4 py-2 rounded ${activeTab === 'transcription' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Transcription
          </button>
          <button
            onClick={() => {
              setActiveTab('fine-tuning');
              fetchFineTuningAudios();
            }}
            className={`px-4 py-2 rounded ${activeTab === 'fine-tuning' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Fine-tuning Audios
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-6">
        {activeTab === 'transcription' && (
          <>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-10 text-center bg-white">
              <p className="text-gray-600 mb-4">Drag and drop your audio file here, or</p>
              <input
                type="file"
                accept="audio/*"
                ref={fileInputRef}
                onChange={handleFileUpload}
                className="hidden"
                id="fileUploadInput"
              />
              <label
                htmlFor="fileUploadInput"
                className="cursor-pointer inline-block bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
              >
                Browse File
              </label>
            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
              {transcripts.map((t, index) => (
                <TranscriptCard
                  key={index}
                  transcriptData={t}
                  onSubmitCorrection={handleSubmitCorrection}
                />
              ))}
            </div>
          </>
        )}

        {activeTab === 'fine-tuning' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {fineTuningAudios.length === 0 ? (
              <p className="text-gray-600">No fine-tuning audios found.</p>
            ) : (
              fineTuningAudios.map((audio, index) => (
                <div key={index} className="bg-white p-4 rounded-lg shadow">
                  <audio controls src={audio.audio_url} className="mb-2 w-full" />
                  <p className="text-sm"><strong>Transcript:</strong> {audio.audio_text}</p>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function TranscriptCard({ transcriptData, onSubmitCorrection }) {
  const textareaRef = useRef();
  const buttonRef = useRef();

  const audioUrl = URL.createObjectURL(transcriptData.audioBlob);

  return (
    <div className="bg-white p-4 rounded-lg shadow">
      <audio controls src={audioUrl} className="mb-2 w-full" />
      <p className="text-sm mb-1"><strong>Raw Transcript:</strong> {transcriptData.rawTranscript}</p>
      <textarea
        ref={textareaRef}
        defaultValue={transcriptData.correctedTranscript}
        className="w-full h-24 p-2 border rounded mb-2 text-sm"
      />
      <button
        ref={buttonRef}
        onClick={() =>
          onSubmitCorrection(transcriptData.audioBlob, textareaRef.current.value, buttonRef, textareaRef)
        }
        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 w-full text-sm"
      >
        Submit (Complexity Score: {transcriptData.complexityTermsScore})
      </button>
    </div>
  );
}
